import { ActionType } from '@/enums/actionType';
import type { Note, NoteId, NotesAction, NotesSnapshot, NotesState } from '@/types/note';
import { assertNever } from '@/utils/assertNever';
import { nextZIndex } from '@/utils/zOrder';
import { HISTORY_LIMIT } from '@/constants/notes';

export const initialNotesState: NotesState = {
  byId: {},
  order: [],
  past: [],
  future: [],
};

function snapshotOf(state: NotesState): NotesSnapshot {
  return { byId: state.byId, order: state.order };
}

function withHistoryPush(state: NotesState): Pick<NotesState, 'past' | 'future'> {
  const nextPast =
    state.past.length >= HISTORY_LIMIT
      ? [...state.past.slice(1), snapshotOf(state)]
      : [...state.past, snapshotOf(state)];
  return { past: nextPast, future: [] };
}

function removeFromOrder(order: readonly NoteId[], id: NoteId): NoteId[] {
  return order.filter((x) => x !== id);
}

/**
 * Pure reducer. All state transitions are here so the flow is auditable in one
 * place and easy to test. Uses exhaustive `assertNever` in the default case so
 * a new ActionType at compile time forces a matching branch.
 */
export function notesReducer(state: NotesState, action: NotesAction): NotesState {
  switch (action.type) {
    case ActionType.Hydrate: {
      const byId: Record<string, Note> = {};
      const order: NoteId[] = [];
      // Order by zIndex ascending so paint order matches z-order.
      const sorted = [...action.payload.notes].sort((a, b) => a.zIndex - b.zIndex);
      for (const note of sorted) {
        byId[note.id] = note;
        order.push(note.id);
      }
      return { byId, order, past: [], future: [] };
    }

    case ActionType.Create: {
      const now = Date.now();
      const note: Note = {
        id: action.payload.id,
        x: action.payload.x,
        y: action.payload.y,
        width: action.payload.width,
        height: action.payload.height,
        color: action.payload.color,
        text: action.payload.text,
        zIndex: action.payload.zIndex ?? nextZIndex(state.byId),
        version: 0, // 0 = local-only until server confirms
        createdAt: now,
        updatedAt: now,
      };
      return {
        ...state,
        ...withHistoryPush(state),
        byId: { ...state.byId, [note.id]: note },
        order: [...state.order, note.id],
      };
    }

    case ActionType.Move: {
      const existing = state.byId[action.payload.id];
      if (!existing) return state;
      if (existing.x === action.payload.x && existing.y === action.payload.y) return state;
      const updated: Note = {
        ...existing,
        x: action.payload.x,
        y: action.payload.y,
        updatedAt: Date.now(),
      };
      return {
        ...state,
        ...withHistoryPush(state),
        byId: { ...state.byId, [existing.id]: updated },
      };
    }

    case ActionType.Resize: {
      const existing = state.byId[action.payload.id];
      if (!existing) return state;
      const updated: Note = {
        ...existing,
        x: action.payload.x ?? existing.x,
        y: action.payload.y ?? existing.y,
        width: action.payload.width,
        height: action.payload.height,
        updatedAt: Date.now(),
      };
      return {
        ...state,
        ...withHistoryPush(state),
        byId: { ...state.byId, [existing.id]: updated },
      };
    }

    case ActionType.Delete: {
      const existing = state.byId[action.payload.id];
      if (!existing) return state;
      const nextById = { ...state.byId };
      delete nextById[action.payload.id];
      return {
        ...state,
        ...withHistoryPush(state),
        byId: nextById,
        order: removeFromOrder(state.order, action.payload.id),
      };
    }

    case ActionType.UpdateText: {
      const existing = state.byId[action.payload.id];
      if (!existing) return state;
      if (existing.text === action.payload.text) return state;
      const updated: Note = { ...existing, text: action.payload.text, updatedAt: Date.now() };
      return {
        ...state,
        ...withHistoryPush(state),
        byId: { ...state.byId, [existing.id]: updated },
      };
    }

    case ActionType.UpdateColor: {
      const existing = state.byId[action.payload.id];
      if (!existing) return state;
      if (existing.color === action.payload.color) return state;
      const updated: Note = { ...existing, color: action.payload.color, updatedAt: Date.now() };
      return {
        ...state,
        ...withHistoryPush(state),
        byId: { ...state.byId, [existing.id]: updated },
      };
    }

    case ActionType.BringToFront: {
      const existing = state.byId[action.payload.id];
      if (!existing) return state;
      const nextZ = nextZIndex(state.byId);
      if (existing.zIndex >= nextZ - 1) return state;
      const updated: Note = { ...existing, zIndex: nextZ };
      return {
        ...state,
        byId: { ...state.byId, [existing.id]: updated },
      };
    }

    case ActionType.ReconcileFromServer: {
      const serverNote = action.payload.note;
      const existing = state.byId[serverNote.id];
      // Preserve any newer local mutations by taking the max version.
      const merged: Note = existing && existing.version > serverNote.version
        ? existing
        : serverNote;
      const inOrder = state.order.includes(serverNote.id);
      return {
        ...state,
        byId: { ...state.byId, [serverNote.id]: merged },
        order: inOrder ? state.order : [...state.order, serverNote.id],
      };
    }

    case ActionType.RollbackOptimistic: {
      const { id, previous } = action.payload;
      const nextById = { ...state.byId };
      if (previous) nextById[id] = previous;
      else delete nextById[id];
      const nextOrder = previous
        ? state.order.includes(id) ? state.order : [...state.order, id]
        : removeFromOrder(state.order, id);
      return { ...state, byId: nextById, order: nextOrder };
    }

    case ActionType.Undo: {
      const previous = state.past[state.past.length - 1];
      if (!previous) return state;
      return {
        byId: previous.byId,
        order: [...previous.order],
        past: state.past.slice(0, -1),
        future: [snapshotOf(state), ...state.future],
      };
    }

    case ActionType.Redo: {
      const next = state.future[0];
      if (!next) return state;
      return {
        byId: next.byId,
        order: [...next.order],
        past: [...state.past, snapshotOf(state)],
        future: state.future.slice(1),
      };
    }

    default:
      return assertNever(action);
  }
}
